import React, { useRef, useCallback, useState, useEffect } from "react";
import { Button, CameraShutter, Icon } from "../../../components/design";
import useTranslator from "../../../hooks/useTranslator";
import ReviewScreen from "./ReviewScreen";
import { preloadImageQuality } from "../../../Utils/imageQuality";

interface CaptureScreenProps {
  side: "front" | "back";
  onCapture: (blob: File | Blob) => void;
  onCancel: () => void;
  reviewBeforeSubmit?: boolean;
}

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const CaptureScreen: React.FC<CaptureScreenProps> = ({
  side,
  onCapture,
  onCancel,
  reviewBeforeSubmit = false,
}) => {
  const { _t } = useTranslator();
  const tRef = useRef(_t);
  tRef.current = _t;

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<File | Blob | null>(null);

  useEffect(() => {
    preloadImageQuality();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraReady(true);
      } catch (err: any) {
        if (!cancelled) {
          const message =
            err.name === "NotAllowedError"
              ? tRef.current("mobileCaptureCameraPermission")
              : tRef.current("mobileCaptureCameraError");
          setCameraError(message);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleCapture = useCallback(() => {
    if (isCapturing) return;

    const video = videoRef.current;
    if (!video) return;
    setIsCapturing(true);

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `${side}_${Date.now()}.jpeg`, {
            type: "image/jpeg",
          });
          if (reviewBeforeSubmit) {
            setCapturedImage(file);
          } else {
            onCapture(file);
          }
        }
      },
      "image/jpeg",
      0.92
    );

    setTimeout(() => setIsCapturing(false), 300);
  }, [isCapturing, side, onCapture, reviewBeforeSubmit]);

  const hintText =
    side === "front"
      ? getTranslatedText(
          _t,
          "frameFrontWithinFrame",
          "Frame the front within the frame"
        )
      : getTranslatedText(
          _t,
          "frameBackWithinFrame",
          "Frame the back within the frame"
        );

  if (cameraError) {
    return (
      <div className="mobile-capture-camera-error">
        <div className="mobile-capture-error-icon">!</div>
        <h3>
          {getTranslatedText(_t, "mobileCaptureCameraIssue", "Camera issue")}
        </h3>
        <p>{cameraError}</p>
        <Button size="lg" variant="secondary" onClick={onCancel}>
          {getTranslatedText(_t, "mobileCaptureGoBack", "Go back")}
        </Button>
      </div>
    );
  }

  if (reviewBeforeSubmit && capturedImage) {
    return (
      <ReviewScreen
        image={capturedImage}
        side={side}
        error={null}
        onRetake={() => setCapturedImage(null)}
        onConfirm={() => {
          const confirmedImage = capturedImage;
          setCapturedImage(null);
          onCapture(confirmedImage);
        }}
      />
    );
  }

  return (
    <div className="mobile-capture-camera-screen">
      <div className="mobile-capture-camera-stage">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="mobile-capture-camera-video"
          onLoadedMetadata={() => {
            videoRef.current?.play();
          }}
        />

        <div className="mobile-capture-camera-overlay" aria-hidden="true">
          <div className="mobile-capture-viewfinder">
            <Icon
              name="idCard"
              size={56}
              className="mobile-capture-viewfinder-guide"
            />
          </div>
        </div>

        <div className="mobile-capture-camera-hint">
          <p>{hintText}</p>
        </div>
      </div>

      <div className="mobile-capture-camera-controls">
        <CameraShutter
          onClick={handleCapture}
          disabled={!cameraReady || isCapturing}
          aria-label={getTranslatedText(
            _t,
            "mobileCaptureShutter",
            "Take photo"
          )}
        />
      </div>
    </div>
  );
};

export default CaptureScreen;
